import { z } from 'zod';

/**
 * Double opt-in for client and therapist registration (ADR-016).
 *
 * WHY
 * The email address is the only identifier this platform holds for a client,
 * and it is what account recovery and every notification depend on. An address
 * that was never confirmed is either a typo — leaving the person unable to get
 * back into an account holding their therapy history — or someone else's
 * mailbox being signed up without their knowledge. Confirming it once, at
 * registration, is what separates those from a real user.
 *
 * Admins are exempt: their accounts are created by an operator script, not by
 * self-service signup, so there is no unknown party to verify.
 */

/** POST /api/v1/auth/verify-email — the token from the emailed link. */
export const VerifyEmailRequestSchema = z.object({
  token: z.string().min(20).max(200),
});
export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;

export const VerifyEmailResponseSchema = z.object({
  verified: z.boolean(),
  /**
   * True when the address was already confirmed — clicking the link twice, or
   * from two devices, is not an error and must not read like one.
   */
  alreadyVerified: z.boolean(),
});
export type VerifyEmailResponse = z.infer<typeof VerifyEmailResponseSchema>;

/**
 * POST /api/v1/auth/resend-verification
 *
 * Deliberately takes the address rather than requiring a session: the user who
 * needs this is the one who cannot log in yet.
 */
export const ResendVerificationRequestSchema = z.object({
  email: z.string().email().max(200),
});
export type ResendVerificationRequest = z.infer<typeof ResendVerificationRequestSchema>;

/**
 * Always the same response, whether or not the address exists.
 *
 * Returning "no such account" here would turn this endpoint into a way to test
 * whether a given person has signed up for therapy — which is precisely the
 * fact this platform exists to keep private.
 */
export const ResendVerificationResponseSchema = z.object({
  accepted: z.literal(true),
});
export type ResendVerificationResponse = z.infer<typeof ResendVerificationResponseSchema>;

/** Hours a confirmation link stays valid. Long enough to survive a night's sleep. */
export const EMAIL_VERIFICATION_TTL_HOURS = 24;

/**
 * The API error code returned when an unverified CLIENT/THERAPIST tries to log
 * in. The apps match on this to show the "check your inbox" screen with a
 * resend button, instead of a generic failure.
 */
export const EMAIL_NOT_VERIFIED_CODE = 'EMAIL_NOT_VERIFIED';
